type FontWeight = {
  weight: number;
  name: string;
};

type FontConfig = {
  path: string;
  weights: FontWeight[];
};

/**
 * Configuration for supported custom fonts.
 * Add new fonts here with their respective file paths and weight mappings.
 */
const FONT_CONFIGS: Record<string, FontConfig> = {
  optimist: {
    path: 'optimist',
    weights: [
      { weight: 100, name: 'Optimist_W_XLt' },
      { weight: 300, name: 'Optimist_W_Lt' },
      { weight: 400, name: 'Optimist_W_Rg' },
      { weight: 600, name: 'Optimist_W_SBd' },
    ],
  },
};

/**
 * Dynamically loads @font-face declarations based on font family name.
 * Currently supports: Optimist
 * System fonts (Arial, Helvetica, etc.) are handled automatically by the browser.
 */
export function loadFonts(fontFamily: string, basePath: string): void {
  // Extract the primary font name from the fontFamily string
  // e.g., "Optimist" from "Optimist, sans-serif"
  const primaryFont = fontFamily.split(',')[0].trim();
  const fontKey = primaryFont.toLowerCase();

  // Check if this is a custom font we need to load
  const fontConfig = FONT_CONFIGS[fontKey];
  if (!fontConfig) {
    // Not a custom font (likely a system font like Arial) - remove any existing dynamic fonts
    const existingStyle = document.getElementById('dynamic-fonts');
    if (existingStyle) {
      existingStyle.remove();
    }
    return;
  }

  const fontPath = `${basePath}${fontConfig.path}`;

  // Create a style element to hold all @font-face rules
  const styleElement = document.createElement('style');
  styleElement.id = 'dynamic-fonts';

  const fontFaceRules = fontConfig.weights
    .map(
      ({ weight, name }) => `
@font-face {
  font-family: ${primaryFont};
  font-style: normal;
  font-weight: ${weight};
  font-display: swap;
  src: url(${fontPath}/${name}.eot);
  src:
    url(${fontPath}/${name}.eot?#iefix) format('embedded-opentype'),
    url(${fontPath}/${name}.woff2) format('woff2'),
    url(${fontPath}/${name}.woff) format('woff'),
    url(${fontPath}/${name}.ttf) format('truetype');
}`,
    )
    .join('\n');

  styleElement.textContent = fontFaceRules;

  // Remove any existing dynamic fonts -- does not load Optimist when config defines Arial
  const existingStyle = document.getElementById('dynamic-fonts');
  if (existingStyle) {
    existingStyle.remove();
  }

  // Append to document head
  document.head.appendChild(styleElement);
}
